<template>
    <v-dialog v-model="dialog" v-stack="{ open: dialog, close: hide }" :max-width="620" :fullscreen="isMobile" persistent content-class="be-editor-dialog">
        <div class="pfe-panel-inner" :class="{ 'pfe-fullscreen': isMobile }">
            <div class="pfe-header">
                <v-btn icon small class="pfe-back" :class="{ 'pfe-back--close': step === 1 }" @click="onBack">
                    <v-icon size="16">{{ step === 1 ? 'mdi-close' : 'mdi-chevron-left' }}</v-icon>
                </v-btn>
                <span class="pfe-title">{{ title }}</span>
                <history-controls v-if="step === 2 && !loadingEdit" context="editor" />
                <div v-else style="width:32px"></div>
            </div>

            <div class="pfe-steps">
                <div class="pfe-step" :class="{ active: step >= 1 }"></div>
                <div class="pfe-step" :class="{ active: step >= 2 }"></div>
            </div>

            <div class="pfe-body">
                <skeleton-loader v-if="loadingEdit" type="rows" :count="5" />

                <!-- PASO 1: DATOS -->
                <template v-else-if="step === 1">
                    <kimsa-text-field :label="$t('common.name')" dense force-value :value="name"
                        @change="name = $event" />
                    <div class="bl-grid2">
                        <kimsa-combobox :label="$t('common.folder')" dense :items="folderOptions" item-text="label"
                            @change="folder = $event" ref="folders" :value="folder" />
                        <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                            :label="$t('exercises.tags')" force-value />
                    </div>

                    <template v-if="isCircuit">
                        <div class="bl-field">
                            <label class="bl-label">{{ $t('common.circuit_type') }}</label>
                            <kimsa-segmented scrollable dense :items="circuitTypeOptions" item-text="label"
                                :value="currentCircuitType" @change="currentCircuitType = $event" />
                        </div>
                        <kimsa-combobox :label="labelSets" :value="sets" dense :items="setOptions" @change="sets = $event"
                            placeholder-class ref="sets" />
                        <kimsa-combobox v-if="isTabata && sets > 1" :label="$t('common.time_pause')" :value="timePause"
                            dense :items="secondOptions" @change="timePause = $event" placeholder-class ref="timeRest" />
                        <div v-if="isTabata" class="bl-grid3">
                            <kimsa-combobox :label="$t('common.laps')" :value="minutes" dense :items="minutesOptions"
                                @change="minutes = $event" placeholder-class ref="minutes" />
                            <kimsa-combobox :label="$t('common.time_work')" :value="timeWork" dense :items="secondOptions"
                                @change="timeWork = $event" placeholder-class ref="timeWork" />
                            <kimsa-combobox :label="$t('common.time_rest')" :value="timeRest" dense :items="secondOptions"
                                @change="timeRest = $event" placeholder-class ref="timeRest" />
                        </div>
                    </template>

                </template>

                <!-- PASO 2: EJERCICIOS -->
                <template v-else>
                    <div class="pfeb-hdr">
                        <div class="pfeb-hdr-info">
                            <div class="pfeb-name">{{ (name || $tc('common.blocks', 1)).toUpperCase() }}</div>
                            <div class="pfeb-meta">
                                <v-icon size="13">mdi-dumbbell</v-icon>
                                <span>{{ exercises.length }}
                                    {{ $tc('common.exercises', exercises.length).toLowerCase() }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="exercises.length === 0" class="be-empty">{{ $t('exercises.no_exercises_set') }}</div>

                    <draggable v-model="exercises" draggable=".es-wrap" handle=".es-drag" ghost-class="ghost">
                        <div v-for="(exercise, i) in exercises" :key="`${exercise.uid}-${historyRev}`" class="es-wrap"
                            :class="{ open: expandedIndex === i }">
                            <div class="es-ej-item" @click="toggleExpand(i)">
                                <span class="es-drag" @click.stop><v-icon size="16">mdi-drag-vertical</v-icon></span>
                                <exercise-img-video class="es-ej-thumb" :img="rawImg(exercise)" :name="exercise.name"
                                    :video="videoUrl(exercise)" :is-youtube-video="!!exercise.youtubeUrl" is-second-dialog
                                    @click.native.stop />
                                <div class="es-ej-info">
                                    <div class="es-ej-name">{{ exercise.name }}</div>
                                    <div class="es-ej-sets" :class="{ 'es-ej-sets-error': isUnconfigured(exercise) }">
                                        {{ setsSummary(exercise) }}
                                    </div>
                                </div>
                                <v-menu offset-y>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon small class="es-ej-ctx" v-on="on" v-bind="attrs" @click.stop>
                                            <v-icon size="18">mdi-dots-horizontal</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item link @click="changeExerciseAt(i)">
                                            <v-icon size="18" color="txtSecondary" class="mr-2">mdi-swap-horizontal</v-icon>
                                            <v-list-item-title>{{ $t('common.change') }}</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item link @click="duplicateExerciseAt(i)">
                                            <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                                            <v-list-item-title>{{ $t('common.duplicate') }}</v-list-item-title>
                                        </v-list-item>
                                        <v-divider />
                                        <v-list-item link @click="confirmDeleteExercise(i)">
                                            <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                                            <v-list-item-title class="error--text">{{ $t('common.delete') }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                                <v-icon size="18" class="es-ej-chev" :class="{ open: expandedIndex === i }">
                                    mdi-chevron-down
                                </v-icon>
                            </div>
                            <div v-if="expandedIndex === i" class="es-ej-detail">
                                <exercise-series-editor :exercise="exercise"
                                    :category-exercise="exerciseCategory(exercise)"
                                    :circuit-type="isCircuit ? currentCircuitType : null"
                                    @update="onExerciseUpdate(i, $event)" @click:rest="setRest(i)" />
                            </div>
                        </div>
                    </draggable>

                    <v-btn text :color="accentColor" class="pfeb-add-ej" @click="showAddExercisesDialog">
                        <v-icon left size="16">mdi-plus</v-icon>{{ $t('common.add_exercise') }}
                    </v-btn>
                </template>

                <kimsa-upload-file v-show="!loadingEdit && step === 1" :label="$t('common.image')"
                    relative-path="images/blocks" mime-types="image/*" :preview-img="previewImg" ref="img" height="150"
                    @change="file = $event; deleteImg = false;" @delete="onDeleteImg" />
            </div>

            <div class="pfe-footer" :class="{ 'pfe-footer--single': step === 1 }">
                <v-btn v-if="step === 2" text tile class="pfe-btn-cancel" @click="hide">
                    {{ $t('common.cancel') }}
                </v-btn>
                <v-btn tile depressed color="primary" class="pfe-btn-next black--text" :disabled="nextDisabled"
                    :loading="saving" @click="onNext">
                    {{ nextLabel }}
                </v-btn>
            </div>

            <rest-dialog ref="setRestDialog" is-second-dialog @save="onRestSave" />
            <add-exercises-dialog ref="addExerciseDialog" is-second-dialog :title="$tc('common.blocks', 1)"
                :one-exercise="oneExercise" :default-filter="defaultFilter" @change="addExercises" />
            <kimsa-confirm ref="confirmDeleteEx" is-second-dialog alert-mode :title="$t('common.delete')"
                :content="$t('common.confirm_delete')" @confirm="doDeleteExercise" @cancel="pendingDeleteEx = null" />
        </div>
    </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db } from "@/utils/Firebase";
import { getPayloadBlock, getMergedBlocks } from "@/utils/Exercises";
import { randomString } from "@/utils/Common";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaUploadFile from "@/components/common/inputs/KimsaUploadFile";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTagsPicker from "@/components/common/inputs/KimsaTagsPicker";
import KimsaSegmented from "@/components/common/inputs/KimsaSegmented";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import ExerciseSeriesEditor from "@/components/coach/routines/ExerciseSeriesEditor";
import RestDialog from "@/components/coach/routines/RestDialog";
import AddExercisesDialog from "@/components/coach/routines/AddExercisesDialog";
import ExerciseImgVideo from "@/components/common/routines/ExerciseImgVideo";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import draggable from "vuedraggable";
import HistoryControls from "@/components/common/HistoryControls";
import editorHistoryMixin from "@/mixins/editorHistoryMixin";

export default {
    name: "BlockEditor",
    components: {
        KimsaTextField, KimsaUploadFile, KimsaCombobox, KimsaTagsPicker, KimsaSegmented,
        SkeletonLoader, ExerciseSeriesEditor, RestDialog, AddExercisesDialog, ExerciseImgVideo, KimsaConfirm, draggable,
        HistoryControls,
    },
    mixins: [editorHistoryMixin],
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            step: 1,
            saving: false,
            loadingEdit: false,
            editMode: false,
            originalIsCircuit: false,
            blockRef: null,
            blockId: null,
            name: '',
            folder: null,
            tags: [],
            file: null,
            deleteImg: false,
            exercises: [],
            expandedIndex: null,
            pendingDeleteEx: null,
            oneExercise: false,
            exerciseIndex: null,
            activeExerciseIndex: null,
            defaultFilter: null,

            currentCircuitType: 'emom',
            sets: 1,
            minutes: 1,
            timeWork: 1,
            timeRest: 1,
            timePause: 1,
        }
    },
    computed: {
        ...mapState(['folders', 'globalFolders']),
        title(vm = this) {
            if (vm.step === 2) return vm.$tc('common.exercises', 2)
            return `${vm.editMode ? vm.$t('common.edit') : vm.$t('common.create')} ${vm.$tc('common.blocks', 1)}`
        },
        nextLabel(vm = this) {
            if (vm.step === 2) return vm.$t('common.save')
            return vm.$t('common.next')
        },
        previewImg(vm = this) {
            return (!vm.deleteImg && vm.blockRef?.img) || vm.file?.img || null
        },
        folderOptions(vm = this) {
            let options = [...vm.globalFolders, ...vm.folders].filter(folder => folder.type === 'blocks')
            if (vm.editMode) options = options.filter(folder => (folder.isCircuit === true) === vm.originalIsCircuit)
            return options.map(folder => ({ ...folder, id: folder.id, label: vm.folderLabel(folder) })) || []
        },
        isCircuit(vm = this) {
            return vm.folder?.isCircuit === true
        },
        isTabata(vm = this) {
            return vm.currentCircuitType === 'tabata'
        },
        accentColor(vm = this) {
            return vm.$vuetify.theme.dark ? 'primary' : 'accent'
        },
        circuitTypeOptions(vm = this) {
            return [
                { label: vm.$t('circuits.emom'), value: 'emom' },
                { label: vm.$t('circuits.amrap'), value: 'amrap' },
                { label: vm.$t('circuits.time'), value: 'time' },
                { label: vm.$t('circuits.tabata'), value: 'tabata' },
            ]
        },
        labelSets(vm = this) {
            if (vm.currentCircuitType === 'emom' || vm.currentCircuitType === 'amrap') return vm.$tc('common.time')
            if (vm.currentCircuitType === 'time') return vm.$tc('common.laps')
            return vm.$tc('exercises.sets', 2)
        },
        setOptions(vm = this) {
            let quantity = 10
            if (vm.currentCircuitType === 'emom') quantity = 15
            if (vm.currentCircuitType === 'amrap') quantity = 25
            return Array.from({ length: quantity }, (_, i) => i + 1);
        },
        minutesOptions() {
            return Array.from({ length: 10 }, (_, i) => i + 1);
        },
        secondOptions() {
            return Array.from({ length: 60 }, (_, i) => i + 1);
        },
        step1Disabled(vm = this) {
            if (!vm.name) return true
            if (vm.isCircuit) {
                if (!vm.sets) return true
                if (vm.isTabata) return !vm.minutes || !vm.timeWork || !vm.timeRest || (vm.sets > 1 && !vm.timePause)
            }
            return false
        },
        step2Disabled(vm = this) {
            if (vm.exercises.length === 0) return true
            if (vm.isCircuit) {
                if (vm.isTabata) return false
                return vm.exercises.some(ex => !ex.config || !ex.config.type || ex.config.value === null || ex.config.value === undefined || ex.config.value === '')
            }
            return vm.exercises.some(ex => ex.type !== 'rest' && (!ex.config || ex?.config?.sets?.length === 0))
        },
        nextDisabled(vm = this) {
            if (vm.saving) return true
            return vm.step === 1 ? vm.step1Disabled : vm.step2Disabled
        },
    },
    async mounted() {
        const vm = this
        if (vm.folders.length === 0) await vm.bindFolders(vm.coachId)
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
    },
    methods: {
        ...mapActions(['bindFolders', 'bindGlobalFolders']),
        showCreate() {
            const vm = this
            vm.reset()
            vm.editMode = false
            vm.startEditorHistory('exercises')
            vm.show()
        },
        async showEdit(block) {
            const vm = this
            vm.reset()
            vm.editMode = true
            vm.show()
            await vm.withLoading(async () => {
            const id = block.objectID || block.id
            vm.blockId = id
            const snap = await db.collection('blocks').doc(id).get()
            if (!snap.exists) {
                vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
                return vm.hide()
            }
            vm.blockRef = snap.data()
            vm.originalIsCircuit = vm.blockRef.block?.isCircuit || vm.blockRef.isCircuit || false
            vm.name = vm.blockRef.name || ''
            if (vm.blockRef.folder) {
                const folder = vm.folderOptions.find(f => f.id === vm.blockRef.folder) || {}
                vm.folder = { id: vm.blockRef.folder, ...folder }
            } else {
                vm.folder = null
            }
            vm.tags = vm.blockRef.tags || []
            vm.currentCircuitType = vm.blockRef.block?.type || 'emom'
            vm.sets = vm.blockRef.block?.sets || 1
            if (vm.blockRef.block?.tabata) {
                vm.minutes = vm.blockRef.block.tabata.minutes || 1
                vm.timeWork = vm.blockRef.block.tabata.timeWork || 1
                vm.timeRest = vm.blockRef.block.tabata.timeRest || 1
                vm.timePause = vm.blockRef.block.tabata.timePause || 1
            }
            const merged = await getMergedBlocks([vm.blockRef.block])
            vm.exercises = (merged[0]?.exercises || []).map(ex => ({ ...ex, uid: ex.uid || randomString() }))
            if (vm.dialog) vm.startEditorHistory('exercises')
            }, 'loadingEdit')
        },
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.stopEditorHistory()
            vm.reset()
        },
        reset() {
            const vm = this
            vm.step = 1
            vm.saving = false
            vm.loadingEdit = false
            vm.editMode = false
            vm.originalIsCircuit = false
            vm.blockRef = null
            vm.blockId = null
            vm.name = ''
            if (vm.$refs.folders) vm.$refs.folders.model = null
            if (vm.$refs.img) vm.$refs.img.reset()
            vm.folder = null
            vm.tags = []
            vm.file = null
            vm.deleteImg = false
            vm.exercises = []
            vm.currentCircuitType = 'emom'
            vm.sets = 1
            vm.minutes = 1
            vm.timeWork = 1
            vm.timeRest = 1
            vm.timePause = 1
        },
        onBack() {
            const vm = this
            if (vm.step === 2) return vm.step = 1
            vm.hide()
        },
        onNext() {
            const vm = this
            if (vm.nextDisabled) return
            if (vm.step === 1) return vm.step = 2
            vm.save()
        },
        onDeleteImg() {
            this.deleteImg = true
        },
        showAddExercisesDialog() {
            const vm = this
            vm.oneExercise = false
            vm.$refs.addExerciseDialog.show()
        },
        addExercises(exercises) {
            const vm = this
            if (vm.oneExercise) return vm.exercises.splice(vm.exerciseIndex, 1, exercises[0])
            let newExercises = exercises.map(ex => ({ ...ex, type: 'exercise', uid: randomString() }))
            vm.exercises = [...vm.exercises, ...newExercises]
        },
        toggleExpand(index) {
            this.expandedIndex = this.expandedIndex === index ? null : index
        },
        exerciseCategory(exercise) {
            return exercise?.categories?.[0] || exercise?.category || null
        },
        isUnconfigured(exercise) {
            const vm = this
            if (vm.isCircuit) {
                if (vm.isTabata) return false
                return !exercise.config || !exercise.config.type || exercise.config.value === null || exercise.config.value === undefined || exercise.config.value === ''
            }
            return !(exercise.config && exercise.config.sets && exercise.config.sets.length > 0)
        },
        setsSummary(exercise) {
            const vm = this
            if (vm.isCircuit) {
                if (vm.isTabata) return vm.$t('circuits.tabata')
                const value = exercise?.config?.value
                const type = vm.$t(`exercises.short_${exercise?.config?.type || 'reps'}`)
                return `${vm.sets} × ${value != null ? value : ''} ${type}`.trim()
            }
            const sets = exercise?.config?.sets || []
            if (!sets.length) return vm.$t('exercises.no_config_exercise_yet')
            const first = sets[0]?.data?.[0]
            const typeKey = (first?.short_name || 'short_reps').replace('short_', '')
            const typeLabel = vm.$t(`exercises.${typeKey}`).toLowerCase()
            let summary = `${sets.length} × ${first && first.value != null ? first.value : ''} ${typeLabel}`
            if (exercise.eachSide) summary += ` · ${vm.$t('exercises.each_side')}`
            return summary
        },
        rawImg(exercise) {
            if (exercise?.youtubeUrl) return this.getYoutubeImgUrl(exercise.youtubeUrl)
            return exercise?.img || exercise?.video?.thumb || null
        },
        videoUrl(exercise) {
            return exercise?.video?.url || exercise?.youtubeUrl || null
        },
        duplicateExerciseAt(index) {
            const vm = this
            const copy = { ...JSON.parse(JSON.stringify(vm.exercises[index])), uid: randomString() }
            vm.exercises.splice(index + 1, 0, copy)
        },
        changeExerciseAt(index) {
            const vm = this
            const exercise = vm.exercises[index]
            vm.exerciseIndex = index
            vm.oneExercise = true
            vm.defaultFilter = { categories: exercise.categories?.[0], types: exercise.types }
            vm.$refs.addExerciseDialog.show()
        },
        confirmDeleteExercise(index) {
            const vm = this
            vm.pendingDeleteEx = index
            vm.$refs.confirmDeleteEx.show()
        },
        doDeleteExercise() {
            const vm = this
            if (vm.pendingDeleteEx !== null) vm.deleteExercise(vm.pendingDeleteEx)
            vm.pendingDeleteEx = null
        },
        deleteExercise(index) {
            const vm = this
            vm.exercises.splice(index, 1)
            if (vm.expandedIndex === index) vm.expandedIndex = null
        },
        setRest(index = null) {
            const vm = this
            if (index >= 0) {
                vm.activeExerciseIndex = index
                let time = vm.exercises[index]?.time || '00:00:00'
                vm.$refs.setRestDialog.showEdit(time)
            } else {
                vm.$refs.setRestDialog.show()
            }
        },
        onRestSave(time) {
            const vm = this
            vm.exercises[vm.activeExerciseIndex].hasTime = true
            vm.exercises[vm.activeExerciseIndex].time = time
        },
        onExerciseUpdate(index, patch) {
            const vm = this
            vm.exercises.splice(index, 1, { ...vm.exercises[index], ...patch })
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
            let block = {
                completed: false,
                exercises: vm.exercises,
                sets: vm.sets,
                isCircuit: vm.isCircuit,
                type: vm.currentCircuitType,
            }
            if (vm.isTabata) {
                block.tabata = {
                    minutes: vm.minutes,
                    timeWork: vm.timeWork,
                    timeRest: vm.timeRest,
                    timePause: vm.timePause,
                    sets: vm.sets,
                }
                block.exercises = vm.exercises.map(ex => ({
                    ...ex,
                    config: { ...(ex.config || {}), type: 'time', value: vm.timeWork }
                }))
            }
            let img = null
            if (vm.editMode && vm.blockRef?.img && !vm.deleteImg) img = { img: vm.blockRef.img, imgSavePath: vm.blockRef.imgSavePath }
            if (vm.file) img = await vm.$refs.img.upload()
            const folder = vm.folder || { id: null, name: null }
            let payload = getPayloadBlock(vm.name, vm.coachId, block, folder, vm.tags, img)

            if (vm.editMode) {
                await db.collection('blocks').doc(vm.blockId).update(payload)
                vm.toast(vm.$t('common.saved_block'))
                vm.$emit('success', { objectID: vm.blockId, ...payload })
            } else {
                const created = await db.collection('blocks').add(payload)
                vm.toast(vm.$t('circuits.saved'))
                vm.$emit('success', { objectID: created.id, ...payload })
            }
            vm.hide()
            }, 'saving')
        },
    },
    watch: {
        coachId(val) {
            if (val) this.bindFolders(val)
        },
    },
}
</script>

<style scoped>
.pfe-panel-inner {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 16px;
    overflow: hidden;
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.pfe-fullscreen {
    height: 100%;
    max-height: none;
    border-radius: 0;
    border: none;
}

.pfe-fullscreen .pfe-header {
    padding-top: calc(14px + var(--safe-area-inset-top, 0px));
}

.pfe-fullscreen .pfe-footer>.v-btn {
    height: calc(52px + var(--safe-area-inset-bottom, 0px)) !important;
    padding-bottom: var(--safe-area-inset-bottom, 0px);
}

.pfe-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    flex-shrink: 0;
    background: var(--v-surface-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.pfe-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.pfe-back {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
}

.pfe-back .v-icon {
    color: inherit !important;
}

.pfe-back--close {
    background: rgba(232, 92, 92, 0.12) !important;
    color: var(--v-error-base) !important;
}

.pfe-steps {
    display: flex;
    gap: 6px;
    padding: 12px 16px;
    background: var(--v-surface-base);
    flex-shrink: 0;
}

.pfe-step {
    flex: 1;
    height: 3px;
    border-radius: 2px;
    background: var(--v-outline-base);
}

.theme--dark .pfe-step.active {
    background: var(--v-primary-base);
}

.theme--light .pfe-step.active {
    background: var(--v-accent-base);
}

.pfe-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

@media (max-width: 959px) {
    .pfe-body::-webkit-scrollbar {
        display: none;
    }
}

.bl-grid2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-items: start;
}

.bl-grid3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 12px;
    align-items: start;
}

.bl-field {
    display: flex;
    flex-direction: column;
}

.bl-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    margin-bottom: 6px;
}

.be-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 24px 0;
}

.ghost {
    opacity: 0.5;
}

.pfeb-hdr {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 12px;
}

.theme--dark .pfeb-hdr {
    background: linear-gradient(135deg, #1a1f2e 0%, #141824 100%);
    border: 1px solid rgba(204, 255, 51, 0.12);
}

.theme--light .pfeb-hdr {
    background: linear-gradient(135deg, #f0f4f8, #e8edf4);
    border: 1px solid rgba(51, 153, 153, 0.15);
}

.pfeb-hdr::before {
    content: '1';
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 56px;
    font-weight: 800;
    line-height: 1;
    pointer-events: none;
}

.theme--dark .pfeb-hdr::before {
    color: rgba(204, 255, 51, 0.05);
}

.theme--light .pfeb-hdr::before {
    color: rgba(51, 153, 153, 0.06);
}

.pfeb-hdr-info {
    flex: 1;
    min-width: 0;
    position: relative;
}

.pfeb-name {
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.theme--dark .pfeb-name {
    color: var(--v-primary-base);
}

.theme--light .pfeb-name {
    color: var(--v-accent-base);
}

.pfeb-meta {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.pfeb-meta .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.es-wrap {
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 8px;
    background: var(--v-surface-base);
}

.es-wrap.open {
    border-color: var(--v-outline2-base);
}

.es-ej-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    cursor: pointer;
    transition: background 0.12s;
}

.es-ej-item:hover {
    background: var(--v-surface2-base);
}

.es-drag {
    display: flex;
    cursor: grab;
    color: var(--v-txtMuted-base);
}

.es-drag .v-icon {
    color: inherit !important;
}

.es-ej-thumb {
    flex: 0 0 44px !important;
    max-width: 44px !important;
    min-width: 44px !important;
    height: 38px !important;
    padding: 0 !important;
    border-radius: 8px !important;
    flex-shrink: 0;
}

.es-ej-info {
    flex: 1;
    min-width: 0;
}

.es-ej-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.es-ej-sets {
    font-size: 11px;
    margin-top: 2px;
    font-weight: 600;
}

.theme--dark .es-ej-sets {
    color: var(--v-primary-base);
}

.theme--light .es-ej-sets {
    color: var(--v-accent-base);
}

.theme--dark .es-ej-sets-error,
.theme--light .es-ej-sets-error {
    color: var(--v-error-base);
}

.es-ej-ctx {
    color: var(--v-txtMuted-base) !important;
}

.es-ej-chev {
    color: var(--v-txtMuted-base) !important;
    transition: transform 0.2s;
    flex-shrink: 0;
}

.es-ej-chev.open {
    transform: rotate(180deg);
}

.es-ej-detail {
    padding: 6px 12px 14px;
    border-top: 1px solid var(--v-outline-base);
}

.pfeb-add-ej {
    text-transform: none !important;
    letter-spacing: normal !important;
    justify-content: flex-start !important;
    padding-left: 10px !important;
}

.pfe-footer {
    display: grid;
    grid-template-columns: 1fr 2fr;
    border-top: 1px solid var(--v-outline-base);
    flex-shrink: 0;
    overflow: hidden;
}

.pfe-footer--single {
    grid-template-columns: 1fr;
}

.pfe-footer>.v-btn {
    width: 100% !important;
    min-width: 0 !important;
    height: 52px !important;
    border-radius: 0 !important;
    text-transform: none !important;
    letter-spacing: normal !important;
}

.pfe-btn-cancel {
    background: var(--v-surface2-base) !important;
    border-right: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base) !important;
    font-size: 14px;
    font-weight: 600;
}

.pfe-btn-next {
    font-size: 15px;
    font-weight: 700;
}

@media (max-width: 600px) {

    .bl-grid2,
    .bl-grid3 {
        grid-template-columns: 1fr;
    }

    .pfe-body {
        padding: 16px 14px;
        gap: 18px;
    }

    .es-ej-item {
        padding: 10px;
    }

    .es-ej-detail {
        padding: 8px 12px 16px;
    }
}
</style>
